import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { jwtDecode } from "jwt-decode";
import { GENERIC_ERROR_MESSAGE, logApiError, logUnexpectedError } from "../Utils/apiError";

const RECAPTCHA_SITE_KEY = "6LdrkZArAAAAAFagTf1DUI77pb59JT81RJTLNHRP";

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get("redirect") || "/";

  const waitForRecaptcha = async () => {
    for (let i = 0; i < 30; i++) {
      if (window.grecaptcha && window.grecaptcha.execute) return true;
      await new Promise((res) => setTimeout(res, 100));
    }
    return false;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setError("");
    setSubmitting(true);

    const recaptchaReady = await waitForRecaptcha();
    if (!recaptchaReady) {
      setSubmitting(false);
      setError("Captcha not loaded. Please refresh the page and try again.");
      return;
    }

    try {
      const captchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: "login" });

      const response = await fetch(`${process.env.REACT_APP_API_BASE}/public/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, captchaToken }),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.clone().json();
        } catch {
          errorData = {};
        }

        if (response.status === 401 && errorData?.error === "email_not_verified") {
          localStorage.setItem("pendingVerificationEmail", email);
          navigate("/check-email");
          return;
        }

        await logApiError(response, "Login failed");
        let message = GENERIC_ERROR_MESSAGE;
        if (response.status === 401) {
          message = "Invalid Credentials";
        } else if (response.status === 429) {
          message = "Too many attempts, please try again shortly";
        }
        setError(message);
        return;
      }

      window.gtag?.("event", "login", {
        method: "email",
      });
      window.fbq?.("track", "Login");

      const data = await response.json();
      if (!data?.token) throw new Error("No token returned");

      localStorage.setItem("token", data.token);

      let decoded = {};
      try {
        decoded = jwtDecode(data.token) || {};
      } catch {
        decoded = {};
      }

      const id =
        decoded.userId !== undefined && decoded.userId !== null
          ? decoded.userId
          : decoded.id !== undefined && decoded.id !== null
            ? decoded.id
            : null;

      const userEmail = decoded.email ?? decoded.upn ?? decoded.sub ?? null;

      const firstName =
        decoded.firstName ??
        decoded.given_name ??
        decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"] ??
        "";

      const lastName =
        decoded.lastName ??
        decoded.family_name ??
        decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname"] ??
        "";

      const deriveNameFromEmail = (em) => {
        if (!em) return "";
        const local = (em.split("@")[0] || "")
          .replace(/\d+/g, " ")
          .replace(/[^\w\s.-]/g, " ")
          .trim();
        return local
          .split(/[\s._-]+/)
          .filter(Boolean)
          .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
          .join(" ");
      };

      const nameFromToken =
        decoded.name && typeof decoded.name === "string" && decoded.name.trim()
          ? decoded.name.trim()
          : firstName || lastName
            ? `${firstName} ${lastName}`.trim()
            : deriveNameFromEmail(userEmail);

      const userObj = {
        id,
        email: userEmail,
        firstName,
        lastName,
        name: nameFromToken,
      };

      localStorage.setItem("user", JSON.stringify(userObj));
      login(userObj, data.token);

      navigate(redirectPath || "/", { replace: true });
    } catch (err) {
      logUnexpectedError(err, "Login request failed");
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-5xl items-center">
        <div className="grid w-full gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <section className="hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-sm lg:flex lg:flex-col lg:justify-between">
            <div>
              <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
                Meshum
              </div>
              <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">
                Welcome back to Meshum
              </h1>
              <p className="mt-3 max-w-xl text-sm leading-6 text-slate-600">
                Sign in to save cars, compare market insights, and continue your search with the same trusted pricing context.
              </p>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              <TrustTile title="Save cars" text="Keep track of listings you want to revisit." />
              <TrustTile title="Compare value" text="See asking price alongside Meshum Estimate context." />
              <TrustTile title="Continue search" text="Pick up where you left off across devices." />
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-6">
              <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 lg:hidden">
                Meshum
              </div>
              <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                Welcome back to Meshum
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Sign in to save cars, compare market insights, and continue your search.
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <Field label="Email">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </Field>

              <Field label="Password">
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </Field>

              {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="h-12 w-full rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Logging in..." : "Log in"}
              </button>
            </form>

            <div className="mt-5 flex flex-col items-center gap-3 text-sm sm:flex-row sm:justify-between">
              <a href="/forgot-password" className="font-medium text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-800">
                Forgot password?
              </a>
              <p className="text-slate-600">
                Don&apos;t have an account?{" "}
                <a href="/register" className="font-medium text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-800">
                  Register here
                </a>
              </p>
            </div>

            <p className="mt-6 text-xs leading-5 text-slate-500">
              Meshum uses captcha and secure authentication to protect your account and saved listings.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function TrustTile({ title, text }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <p className="mt-1 text-sm leading-6 text-slate-600">{text}</p>
    </div>
  );
}

export default LoginForm;
